import { FC } from 'react';
import {
  List,
  Filter,
  FunctionField,
  DateField,
  useListPaginationContext,
  ListProps,
  useListContext,
  useGetOne,
  TextInput,
  BooleanField,
} from 'react-admin';
import { useDefineAppLocation } from '@react-admin/ra-navigation';
import { useLocation, Redirect } from 'react-router-dom';
import Datagrid from 'components/Datagrid';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import {
  PhaseStructureTypeEnum,
  SubdivisionAuditInterface,
  SubdivisionDto,
} from '@vatos-pas/common';
import CostStructureHistoryActions from 'components/CostStructureHistoryActions';
import {
  formatProductionPricing,
  getCostColumnsForWorkflow,
  SubdivisionAuditColumn,
  SUBDIVISION_AUDIT_SPECIAL_JOBS_COLUMN,
  SUBDIVISION_AUDIT_ALLOW_PAYOUT_EDITING_COLUMN,
} from '../config/auditColumns';
import { getSubdivisionAuditCsvColumns } from '../config/auditCsvColumns';

const EmptyState = () => (
  <Box padding={2}>
    <Typography>No cost structure changes recorded</Typography>
  </Box>
);

const EmptyPagination = () => {
  const { loaded } = useListContext();
  const { total } = useListPaginationContext();
  if (!loaded || total > 0) return null;
  return <EmptyState />;
};

type AuditListFiltersProps = {
  subdivisionName?: string;
  loading?: boolean;
};

const AuditListFilters: FC<AuditListFiltersProps> = ({
  subdivisionName,
  loading,
  ...props
}) => (
  <Filter {...props}>
    <TextInput
      source="subdivisionId"
      label="Subdivision"
      alwaysOn
      disabled
      format={() => (loading ? 'Loading...' : subdivisionName || '')}
      parse={() => undefined}
    />
  </Filter>
);

type SubdivisionAuditListContentProps = {
  phaseStructureType: PhaseStructureTypeEnum;
  subdivisionId: string;
};

const SubdivisionAuditListContent: FC<SubdivisionAuditListContentProps> = ({
  phaseStructureType,
  subdivisionId,
}) => {
  const { loaded, data, ids } = useListContext();
  const costColumns = getCostColumnsForWorkflow(phaseStructureType);

  // Only render when loaded AND data belongs to current subdivision
  const firstRecord = ids?.[0] ? data?.[ids[0]] : null;
  const dataMatchesFilter =
    !firstRecord || firstRecord.subdivisionId === subdivisionId;
  if (!loaded || !dataMatchesFilter) return null;

  const renderCostColumn = (column: SubdivisionAuditColumn) => (
    <FunctionField<SubdivisionAuditInterface>
      key={column.source}
      label={column.label}
      sortable={column.sortable}
      sortBy={column.source}
      render={record => {
        const value = record?.[column.source];
        return column.format ? column.format(value) : value ?? '-';
      }}
    />
  );

  return (
    <Datagrid isHeaderSelectable={false} isRowSelectable={() => false}>
      <FunctionField<SubdivisionAuditInterface>
        label="Name"
        sortable={true}
        sortBy="changedByUser.lastName"
        render={record =>
          record?.changedByUser
            ? `${record.changedByUser.firstName} ${record.changedByUser.lastName}`
            : '-'
        }
      />
      <FunctionField<SubdivisionAuditInterface>
        label="Production Pricing"
        sortable={false}
        render={record => formatProductionPricing(record?.overrideType)}
      />
      <BooleanField source="overrideRegionCostPerBoard" label="Override" />
      <DateField
        source="createdAt"
        label="Timestamp"
        showTime
        options={{
          day: '2-digit',
          hour: '2-digit',
          hour12: true,
          minute: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }}
        sortable={true}
      />
      {costColumns.map(renderCostColumn)}
      {renderCostColumn(SUBDIVISION_AUDIT_SPECIAL_JOBS_COLUMN)}
      {renderCostColumn(SUBDIVISION_AUDIT_ALLOW_PAYOUT_EDITING_COLUMN)}
    </Datagrid>
  );
};

// Inner component that uses useGetOne - only rendered when subdivisionId exists
type SubdivisionAuditListInnerProps = ListProps & {
  subdivisionId: string;
};

const SubdivisionAuditListInner: FC<SubdivisionAuditListInnerProps> = ({
  subdivisionId,
  ...props
}) => {
  const { data: subdivision, loading: subdivisionLoading } =
    useGetOne<SubdivisionDto>('subdivision', subdivisionId);

  // Define custom app location for breadcrumb
  // Use subdivisionId as fallback when subdivision data hasn't loaded yet (e.g., on refresh)
  useDefineAppLocation('subdivision.edit.audit', {
    record: subdivision ?? { id: subdivisionId },
  });

  const phaseStructureType =
    subdivision?.phaseStructureType || PhaseStructureTypeEnum.Drywall;

  return (
    <List
      {...props}
      resource="subdivision-audit"
      basePath="/subdivision-audit"
      filter={{ subdivisionId }}
      filters={
        <AuditListFilters
          subdivisionName={subdivision?.name}
          loading={subdivisionLoading}
        />
      }
      sort={{ field: 'createdAt', order: 'DESC' }}
      pagination={<EmptyPagination />}
      bulkActionButtons={false}
      actions={
        <CostStructureHistoryActions
          name={subdivision?.name}
          nameFallback={subdivisionId}
          loading={subdivisionLoading}
          columns={getSubdivisionAuditCsvColumns(phaseStructureType)}
          data-cy="SubdivisionAuditExportButton"
        />
      }
      exporter={false}
    >
      <SubdivisionAuditListContent
        phaseStructureType={phaseStructureType}
        subdivisionId={subdivisionId}
      />
    </List>
  );
};

export const SubdivisionAuditList = (props: ListProps) => {
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const filterParam = searchParams.get('filter');
  let subdivisionId: string | null = null;

  if (filterParam) {
    try {
      const filter = JSON.parse(filterParam);
      subdivisionId = filter.subdivisionId || null;
    } catch {
      // Invalid filter format
    }
  }

  if (!subdivisionId) {
    return <Redirect to="/subdivision" />;
  }

  return <SubdivisionAuditListInner subdivisionId={subdivisionId} {...props} />;
};
